/**
 * Client lifecycle + lead-history helpers — the ONE place that decides whether a client
 * is Active / Canceling / Canceled / Lost / Archived, and the ONE place that builds the
 * Firestore patch for moving between those states.
 *
 * Data model (Oct 2026 — see memory "account-lifecycle-separation"):
 *
 *   program.status          the program TIER only (Lite / Plus / Pro / Custom / Hourly / …).
 *                           Never "Lost" or "Archive" any more — those were lifecycle words
 *                           squatting in the tier field and erasing it.
 *   stage (clients)         mirrors the tier; it is what the CRM Clients kanban groups by.
 *   program.clientStatus    the stored lifecycle: 'Active' | 'Canceled' | 'Lost' | 'Archived'.
 *                           'Canceling' is never stored — it is derived from a scheduled cancel
 *                           date (program.lifecycleOverrides / legacy cancelState / Stripe) that
 *                           has not passed yet. Once it passes the client is Canceled, with no
 *                           manual "mark Lost" step.
 *   lostAt / canceledAt / archivedAt   when the transition happened (ISO string).
 *   tierBeforeLost          snapshot of program.status at the moment the client ended.
 *   resultReason            why (Communication / Price / Value / Process) — shared with leads.
 *   reactivatedAt           last time an ended client was flipped back to Active.
 *
 *   leadSnapshot            frozen at lead → client conversion: the pipeline stage, substatus,
 *                           signing chance, lead source and received date the lead had right
 *                           before `stage` was overwritten with the program tier.
 *   convertedToClientAt     when the conversion happened (all three conversion paths).
 *   stageBeforeLost / lostAt (leads)  pipeline stage right before a lead was marked Lost.
 *
 * Legacy documents still carry 'Lost' in program.status / stage / status. Every reader here
 * accepts those markers so old data keeps working until scripts/backfillAccountLifecycle.js
 * has run.
 */

import { resolveAccountLifecycle } from './accountProgramDeliverables';

export const CLIENT_LIFECYCLE = Object.freeze({
  ACTIVE: 'Active',
  CANCELING: 'Canceling',
  CANCELED: 'Canceled',
  LOST: 'Lost',
  ARCHIVED: 'Archived',
});

/** States a user can pick directly. Canceling is derived, never chosen. */
export const STORABLE_CLIENT_LIFECYCLES = Object.freeze(['Active', 'Canceled', 'Lost', 'Archived']);

export const ENDED_CLIENT_LIFECYCLES = new Set(['Canceled', 'Lost', 'Archived']);

/** Program tiers — the only values program.status may hold now. */
export const PROGRAM_TIERS = Object.freeze([
  'Not Started',
  'Lite',
  'Plus',
  'Pro',
  'Hourly',
  '5 Hours',
  '10 Hours',
  'A La Carte',
  'Custom',
]);

export const END_REASONS = Object.freeze(['Communication', 'Price', 'Value', 'Process']);

/** Stripe billing statuses that mean the subscription is over. */
const ENDED_BILLING_STATUSES = new Set(['canceled', 'cancelled', 'unpaid', 'incomplete_expired']);

const LEGACY_LOST_WORDS = new Set(['lost']);
const LEGACY_ARCHIVED_WORDS = new Set(['archive', 'archived']);
const LEGACY_CANCELED_WORDS = new Set(['canceled', 'cancelled', 'inactive']);

const lower = (v) => (typeof v === 'string' ? v.trim().toLowerCase() : '');

export function programObject(account) {
  const p = account?.program;
  if (p && typeof p === 'object') return p;
  if (typeof p === 'string' && p) return { status: p };
  return {};
}

/** A `{ "0": "C", "1": "u", ... }` entry left by spreading a program string into an object. */
export function isProgramCharKey(key, value) {
  return /^\d+$/.test(key) && typeof value === 'string' && value.length === 1;
}

/**
 * `program` as a fresh map with `patch` merged in — build every `program` write with this.
 * Legacy accounts store `program` as a plain status string, and spreading a string yields
 * character keys, so it is promoted to `{ status }` first. Character keys an earlier spread
 * already saved are dropped (their spelled-out word becomes `status` if none is set).
 */
export function programMapWith(program, patch = {}) {
  if (typeof program === 'string') return { ...(program ? { status: program } : {}), ...patch };
  const base = {};
  const chars = [];
  Object.entries(program && typeof program === 'object' ? program : {}).forEach(([k, v]) => {
    if (isProgramCharKey(k, v)) chars[Number(k)] = v;
    else base[k] = v;
  });
  if (chars.length && !base.status) base.status = chars.join('');
  return { ...base, ...patch };
}

export function toDateSafe(value) {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (typeof value === 'object' && typeof value.seconds === 'number') return new Date(value.seconds * 1000);
  const s = String(value);
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  const d = ymd ? new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3])) : new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

function isTierWord(value) {
  const v = lower(value);
  return !!v && PROGRAM_TIERS.some((t) => t.toLowerCase() === v);
}

function titleCase(s) {
  return String(s || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * The program tier, ignoring any lifecycle word that an older write left in program.status.
 * Falls back to tierBeforeLost, then the Stripe plan key, then 'Not Started'.
 */
const tierFor = (value) => PROGRAM_TIERS.find((t) => t.toLowerCase() === lower(value));

/**
 * Tier recovered from statusHistory on a legacy doc whose program.status says "Lost": the
 * previousStatus of the entry that marked it Lost, the program named in the conversion entry,
 * or the last tier-valued status. Null when nothing usable is recorded.
 */
export function recoverTierFromHistory(account) {
  const history = Array.isArray(account?.statusHistory) ? account.statusHistory : [];
  for (let i = history.length - 1; i >= 0; i -= 1) {
    const h = history[i] || {};
    if (isTierWord(h.tier)) return tierFor(h.tier);
    const st = lower(h.status);
    if ((LEGACY_LOST_WORDS.has(st) || LEGACY_ARCHIVED_WORDS.has(st)) && isTierWord(h.previousStatus)) return tierFor(h.previousStatus);
    const m = /converted to client\s*[-–—]\s*program:\s*(.+)$/i.exec(String(h.action || ''));
    if (m && isTierWord(m[1].trim())) return tierFor(m[1].trim());
    if (isTierWord(h.status) && lower(h.status) !== 'not started') return tierFor(h.status);
  }
  return null;
}

/** The tier if one is actually recorded anywhere on the doc; null when it cannot be known. */
export function findKnownTier(account) {
  const p = programObject(account);
  if (isTierWord(p.status)) return tierFor(p.status);
  if (isTierWord(account?.tierBeforeLost)) return tierFor(account.tierBeforeLost);
  if (isTierWord(account?.stage)) return tierFor(account.stage);
  if (isTierWord(p.programName)) return tierFor(p.programName);
  const fromHistory = recoverTierFromHistory(account);
  if (fromHistory) return fromHistory;
  const pk = account?.billing?.planKey;
  if (pk) {
    const guess = titleCase(String(pk).split(/[_-]/)[0]);
    return isTierWord(guess) ? tierFor(guess) : guess;
  }
  return null;
}

/**
 * The program tier, ignoring any lifecycle word that an older write left in program.status.
 * Falls back to tierBeforeLost, stage, statusHistory, the Stripe plan key, then 'Not Started'.
 */
export function getProgramTier(account) {
  return findKnownTier(account) || 'Not Started';
}

/** True when program.status still holds a lifecycle word instead of a tier (pre-backfill doc). */
export function hasLegacyLifecycleInTier(account) {
  const v = lower(programObject(account).status);
  return LEGACY_LOST_WORDS.has(v) || LEGACY_ARCHIVED_WORDS.has(v);
}

/**
 * The lifecycle the document STORES, normalised: 'Active' | 'Canceled' | 'Lost' | 'Archived'.
 * Reads program.clientStatus first, then the legacy markers (program.status / stage / status).
 */
export function getStoredClientLifecycle(account) {
  const p = programObject(account);
  const cs = lower(p.clientStatus);
  if (LEGACY_LOST_WORDS.has(cs)) return CLIENT_LIFECYCLE.LOST;
  if (LEGACY_ARCHIVED_WORDS.has(cs)) return CLIENT_LIFECYCLE.ARCHIVED;
  if (LEGACY_CANCELED_WORDS.has(cs)) return CLIENT_LIFECYCLE.CANCELED;
  if (cs === 'active') return CLIENT_LIFECYCLE.ACTIVE;

  // Legacy markers written before lifecycle and tier were separated.
  const markers = [p.status, account?.stage, account?.status].map(lower);
  if (markers.some((m) => LEGACY_LOST_WORDS.has(m))) return CLIENT_LIFECYCLE.LOST;
  if (markers.some((m) => LEGACY_ARCHIVED_WORDS.has(m))) return CLIENT_LIFECYCLE.ARCHIVED;
  if (LEGACY_CANCELED_WORDS.has(lower(account?.status))) return CLIENT_LIFECYCLE.CANCELED;
  return CLIENT_LIFECYCLE.ACTIVE;
}

/**
 * The effective lifecycle right now.
 *
 * Returns {
 *   state:               'Active' | 'Canceling' | 'Canceled' | 'Lost' | 'Archived'
 *   storedState:         what the document says (never 'Canceling')
 *   isEnded:             state is Canceled / Lost / Archived
 *   isDerived:           true when state differs from storedState (a cancel date passed, or
 *                        Stripe reports the subscription ended) — callers may persist it
 *   cancelEffectiveDate: Date | null  (scheduled or past cancel date)
 *   endedAt:             Date | null  (lostAt / canceledAt / archivedAt / derived cancel date)
 *   reason:              resultReason | null
 *   source:              'stored' | 'cancel-date' | 'stripe'
 * }
 */
export function resolveClientLifecycle(account, now = new Date()) {
  const storedState = getStoredClientLifecycle(account);
  const reason = account?.resultReason || account?.lostReason || null;

  if (ENDED_CLIENT_LIFECYCLES.has(storedState)) {
    const endedAt =
      toDateSafe(account?.lostAt) ||
      toDateSafe(account?.canceledAt) ||
      toDateSafe(account?.archivedAt) ||
      null;
    return {
      state: storedState,
      storedState,
      isEnded: true,
      isDerived: false,
      cancelEffectiveDate: null,
      endedAt,
      reason,
      source: 'stored',
    };
  }

  // Scheduled cancel (CRM override, legacy "Cancels on …", or Stripe cancel_at_period_end).
  let cancelEffectiveDate = null;
  try {
    const lc = resolveAccountLifecycle(account, null);
    if (lc?.cancelType === 'cancels') cancelEffectiveDate = lc.cancelEffectiveDate || null;
  } catch (e) {
    cancelEffectiveDate = null;
  }

  if (cancelEffectiveDate) {
    const passed = cancelEffectiveDate.getTime() <= now.getTime();
    return {
      state: passed ? CLIENT_LIFECYCLE.CANCELED : CLIENT_LIFECYCLE.CANCELING,
      storedState,
      isEnded: passed,
      isDerived: passed,
      cancelEffectiveDate,
      endedAt: passed ? cancelEffectiveDate : null,
      reason,
      source: 'cancel-date',
    };
  }

  // Billing says the subscription is over: Stripe's status when the account is Stripe-linked,
  // otherwise the manual program.subscriptionStatus picked on the Program tab.
  const billing = account?.billing || {};
  const hasStripe = !!billing.stripeSubscriptionId;
  const billingStatus = hasStripe ? billing.status : programObject(account).subscriptionStatus || billing.status;
  if (ENDED_BILLING_STATUSES.has(lower(billingStatus))) {
    const endedAt = toDateSafe(billing.canceledAt) || toDateSafe(billing.endedAt) || toDateSafe(billing.cancelAt) || null;
    return {
      state: CLIENT_LIFECYCLE.CANCELED,
      storedState,
      isEnded: true,
      isDerived: true,
      cancelEffectiveDate: null,
      endedAt,
      reason,
      source: hasStripe ? 'stripe' : 'billing',
    };
  }

  return {
    state: CLIENT_LIFECYCLE.ACTIVE,
    storedState,
    isEnded: false,
    isDerived: false,
    cancelEffectiveDate: null,
    endedAt: null,
    reason,
    source: 'stored',
  };
}

export function isEndedClient(account, now) {
  return resolveClientLifecycle(account, now).isEnded;
}

function isClientType(account) {
  const t = lower(account?.type || account?.accountType);
  return t === 'clients' || t === 'client' || !t;
}

function isDeletedAccount(account) {
  return (
    account?.isDeleted === true ||
    account?.deleted === true ||
    lower(account?.status) === 'deleted'
  );
}

/** A client we are still serving: client type, not ended, not deleted. */
export function isActiveClientAccount(account, now) {
  if (!account) return false;
  if (!isClientType(account)) return false;
  if (isDeletedAccount(account)) return false;
  return !isEndedClient(account, now);
}

/** Chip label + Tailwind classes for a lifecycle state. */
export function describeClientLifecycle(stateOrAccount) {
  const state = typeof stateOrAccount === 'string' ? stateOrAccount : resolveClientLifecycle(stateOrAccount).state;
  switch (state) {
    case CLIENT_LIFECYCLE.CANCELING:
      return { label: 'Canceling', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200' };
    case CLIENT_LIFECYCLE.CANCELED:
      return { label: 'Canceled', className: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200' };
    case CLIENT_LIFECYCLE.LOST:
      return { label: 'Lost', className: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200' };
    case CLIENT_LIFECYCLE.ARCHIVED:
      return { label: 'Archived', className: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200' };
    default:
      return { label: 'Active', className: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200' };
  }
}

/* ── Transitions ─────────────────────────────────────────────────────────── */

function endedAtKeyFor(state) {
  if (state === CLIENT_LIFECYCLE.LOST) return 'lostAt';
  if (state === CLIENT_LIFECYCLE.CANCELED) return 'canceledAt';
  if (state === CLIENT_LIFECYCLE.ARCHIVED) return 'archivedAt';
  return null;
}

/**
 * Build the whole-document patch for moving a client to `to`. Merges onto a copy of the account
 * (the CRM saves whole docs) and NEVER touches program.status or stage, so the tier survives.
 *
 *   to            'Active' | 'Canceled' | 'Lost' | 'Archived'
 *   reason        one of END_REASONS, or null to leave resultReason alone
 *   effectiveAt   Date — when it happened (defaults to now)
 *   by            display name / email for statusHistory
 *   note          optional free text stored on the history entry
 *
 * Returns { account: <merged doc>, entry: <history entry> }.
 */
export function applyClientLifecycleTransition(account, { to, reason, effectiveAt, by, note } = {}) {
  if (!STORABLE_CLIENT_LIFECYCLES.includes(to)) {
    throw new Error(`Lifecycle must be one of ${STORABLE_CLIENT_LIFECYCLES.join(', ')} (got "${to}")`);
  }
  const base = account || {};
  const p = programObject(base);
  const when = effectiveAt instanceof Date && !isNaN(effectiveAt.getTime()) ? effectiveAt : new Date();
  const whenIso = when.toISOString();
  const tier = getProgramTier(base);
  const previous = resolveClientLifecycle(base).state;

  const next = { ...base };
  const program = programMapWith(base.program, { clientStatus: to });

  // Un-squat the tier if an older write left "Lost"/"Archive" in it — only when we actually
  // know the tier; otherwise the legacy word stays and the backfill script reports it.
  const knownTier = findKnownTier(base);
  if (hasLegacyLifecycleInTier(base) && knownTier) program.status = knownTier;
  if (!isTierWord(next.stage) && isTierWord(program.status)) next.stage = program.status;

  if (ENDED_CLIENT_LIFECYCLES.has(to)) {
    next[endedAtKeyFor(to)] = whenIso;
    next.tierBeforeLost = tier;
    if (reason !== undefined && reason !== null) next.resultReason = reason;
  } else {
    // Reactivating. Keep the lost/canceled timestamps as history; just flip the state.
    next.reactivatedAt = whenIso;
    program.cancelState = 'Renews';
    program.lifecycleOverrides = {
      ...(p.lifecycleOverrides || {}),
      cancelState: 'renews',
      cancelEffectiveDate: null,
    };
  }

  next.program = program;

  const entry = {
    status: to,
    previousStatus: previous,
    tier,
    date: new Date().toISOString(),
    effectiveDate: whenIso,
    updatedBy: by || 'Unknown User',
    action:
      to === CLIENT_LIFECYCLE.ACTIVE
        ? `Client reactivated (was ${previous})`
        : `Client marked ${to}${reason ? ` — ${reason}` : ''}`,
    ...(note ? { note } : {}),
  };
  next.statusHistory = [...(Array.isArray(base.statusHistory) ? base.statusHistory : []), entry];

  return { account: next, entry };
}

/**
 * When a scheduled cancel date has passed (or Stripe reports the subscription ended) but the
 * document still says Active, this returns the patch that persists the derived Canceled state.
 * Returns null when nothing needs writing.
 */
export function pendingDerivedCancelPatch(account, { by = 'System', now } = {}) {
  const lc = resolveClientLifecycle(account, now);
  if (!lc.isDerived || lc.state !== CLIENT_LIFECYCLE.CANCELED) return null;
  const { account: merged } = applyClientLifecycleTransition(account, {
    to: CLIENT_LIFECYCLE.CANCELED,
    effectiveAt: lc.endedAt || new Date(),
    by,
    note: lc.source === 'stripe' ? 'Stripe subscription ended' : 'Scheduled cancel date passed',
  });
  return merged;
}

/* ── Lead history ────────────────────────────────────────────────────────── */

const LEAD_TERMINAL_STAGES = new Set(['won', 'lost', 'archived']);

/**
 * Snapshot of the lead right before conversion overwrites `stage` with the program tier.
 * `account` is the LEAD document as it is before any conversion fields are applied.
 */
export function buildLeadSnapshot(account, { by, source = 'crm', program } = {}) {
  const base = account || {};
  const existing = base.leadSnapshot && typeof base.leadSnapshot === 'object' ? base.leadSnapshot : null;
  // Converting twice must not clobber the original snapshot.
  if (existing?.stage) return existing;
  const stage = isTierWord(base.stage) || LEAD_TERMINAL_STAGES.has(lower(base.stage)) ? null : base.stage || null;
  return {
    stage: stage || base.stageBeforeLost || lastLeadStageFromHistory(base) || null,
    substatus: base.substatus || null,
    substatusDates: base.substatusDates && typeof base.substatusDates === 'object' ? { ...base.substatusDates } : null,
    signingChance: base.signingChance || null,
    leadSource: base.leadSource || base.source || null,
    receivedDate: toDateSafe(base.receivedDate)?.toISOString() || null,
    qualified: typeof base.qualified === 'boolean' ? base.qualified : null,
    program: program || null,
    convertedAt: new Date().toISOString(),
    convertedBy: by || null,
    source,
  };
}

/** Fields every conversion path should add on top of its own writes. */
export function leadConversionFields(account, opts = {}) {
  return {
    leadSnapshot: buildLeadSnapshot(account, opts),
    convertedToClientAt: account?.convertedToClientAt || new Date().toISOString(),
  };
}

/** Last non-terminal pipeline stage recorded in statusHistory, if any. */
export function lastLeadStageFromHistory(account) {
  const history = Array.isArray(account?.statusHistory) ? account.statusHistory : [];
  for (let i = history.length - 1; i >= 0; i -= 1) {
    const h = history[i];
    const candidates = [h?.previousStatus, h?.status];
    for (const c of candidates) {
      const v = lower(c);
      if (v && !LEAD_TERMINAL_STAGES.has(v) && !isTierWord(c) && v !== 'portal client' && v !== 'promoted to portal') return c;
    }
  }
  return null;
}

/** Fields to add when a LEAD is marked Lost (stage → 'Lost' or result → 'Lost'). */
export function leadLostFields(account, { now } = {}) {
  const base = account || {};
  const out = {};
  const prevStage = base.stage;
  if (!base.stageBeforeLost && prevStage && !LEAD_TERMINAL_STAGES.has(lower(prevStage))) {
    out.stageBeforeLost = prevStage;
  }
  if (!base.lostAt) out.lostAt = (now instanceof Date ? now : new Date()).toISOString();
  return out;
}

/** True when a client doc still carries anything from its lead days worth showing. */
export function hasLeadHistory(account) {
  if (!account) return false;
  if (account.leadSnapshot?.stage || account.convertedToClientAt) return true;
  if (account.intakeNote || account.discoveryCallSummary || account.analysisCallSummary || account.leadSummary) return true;
  if (account.receivedDate || account.leadSource || account.signingChance) return true;
  const history = Array.isArray(account.statusHistory) ? account.statusHistory : [];
  return history.some((h) => /converted to client/i.test(String(h?.action || '')));
}

/** The conversion moment: explicit field first, then the "Converted to Client" history entry. */
export function getConvertedToClientDate(account) {
  const explicit = toDateSafe(account?.convertedToClientAt) || toDateSafe(account?.leadSnapshot?.convertedAt);
  if (explicit) return explicit;
  const history = Array.isArray(account?.statusHistory) ? account.statusHistory : [];
  const entry = history.find((h) => /converted to client/i.test(String(h?.action || '')));
  return entry ? toDateSafe(entry.date) : null;
}
